import { cn } from '@massilia/ui'
import { MessageCircle, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface ChatMessage {
  content: string
  role: ChatRole
}

type ChatRole = 'assistant' | 'user'

interface ChatWidgetProps {
  className?: string
}

export function ChatWidget({ className }: ChatWidgetProps) {
  // Persist the widget state per-browser so users don't lose the conversation on refresh.
  const STORAGE_KEY = 'massilia.chat.widget.v1'
  const MAX_MESSAGES_TO_KEEP = 10
  const [hasHydrated, setHasHydrated] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<null | string>(null)

  // Used to autoscroll when new messages are appended.
  const messagesEndRef = useRef<HTMLDivElement | null>(null)

  const trimMessages = (msgs: ChatMessage[]) =>
    msgs.length > MAX_MESSAGES_TO_KEEP ? msgs.slice(-MAX_MESSAGES_TO_KEEP) : msgs

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (!raw) {
        setHasHydrated(true)
        return
      }
      const parsed = JSON.parse(raw) as
        | undefined
        | {
            isOpen?: boolean
            messages?: ChatMessage[]
          }
      if (parsed?.messages && Array.isArray(parsed.messages)) {
        setMessages(trimMessages(parsed.messages))
      }
      if (typeof parsed?.isOpen === 'boolean') {
        setIsOpen(parsed.isOpen)
      }
    } catch {
      // Ignore storage errors and start with a fresh state.
    } finally {
      setHasHydrated(true)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    if (!hasHydrated) {
      // Prevent overwriting persisted state with the initial empty render.
      return
    }
    try {
      // Save only the minimum state needed to restore the chat on refresh.
      const payload = JSON.stringify({
        isOpen,
        messages: trimMessages(messages),
      })
      window.localStorage.setItem(STORAGE_KEY, payload)
    } catch {
      // Ignore quota / serialization errors.
    }
  }, [hasHydrated, isOpen, messages])

  useEffect(() => {
    if (!messagesEndRef.current) {
      return
    }

    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    // When the widget is re-opened, `messages.length` may not change.
    // Force the scroll position to the latest message.
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
    })
  }, [isOpen])

  const toggleOpen = () => {
    setIsOpen((prev) => !prev)
    setError(null)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const trimmed = input.trim()
    if (!trimmed || isSending) {
      return
    }

    const MAX_MESSAGE_CHARS = 2000
    if (trimmed.length > MAX_MESSAGE_CHARS) {
      setError(
        `Votre message est trop long (${trimmed.length} caractères). ` +
          `Merci de le raccourcir à moins de ${MAX_MESSAGE_CHARS} caractères.`
      )
      return
    }

    // Append locally first for a responsive UI.
    const userMessage: ChatMessage = { content: trimmed, role: 'user' }
    const nextMessages = trimMessages([...messages, userMessage])

    setMessages(nextMessages)
    setInput('')
    setIsSending(true)
    setError(null)

    try {
      // Backend base URL is shared with the rest of the app via Vite env.
      const apiUrl =
        (import.meta as { env?: { VITE_API_URL?: string } })?.env?.VITE_API_URL ??
        'http://localhost:3333'

      const response = await fetch(`${apiUrl}/chat`, {
        body: JSON.stringify({
          messages: nextMessages,
        }),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const data = (await response.json()) as unknown

      let assistantContent = ''

      if (
        data &&
        typeof data === 'object' &&
        'content' in data &&
        typeof (data as { content: unknown }).content === 'string'
      ) {
        assistantContent = (data as { content: string }).content
      } else if (
        data &&
        typeof data === 'object' &&
        'message' in data &&
        data.message &&
        typeof (data as { message: { content?: unknown } }).message.content === 'string'
      ) {
        assistantContent = (data as { message: { content: string } }).message.content
      } else {
        assistantContent = 'Réponse reçue du serveur, mais au format inattendu.'
      }

      const assistantMessage: ChatMessage = {
        content: assistantContent,
        role: 'assistant',
      }

      setMessages((current) => trimMessages([...current, assistantMessage]))
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Une erreur s'est produite lors de l'envoi."
      setError(message)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <>
      {isOpen ? (
        <div
          className={cn(
            'fixed bottom-24 right-4 z-50 flex h-[520px] w-[420px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl',
            'backdrop-blur-sm',
            className
          )}
        >
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1D95AD] text-white">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-stone-900">Assistant Massilia</span>
                <span className="text-xs text-stone-500">Posez-nous vos questions</span>
              </div>
            </div>
            <button
              aria-label="Fermer le chat"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-stone-500 transition hover:bg-stone-100 hover:text-stone-700"
              onClick={toggleOpen}
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto bg-white px-3 py-2 text-sm">
            {messages.length === 0 ? (
              <p className="mt-2 text-center text-xs text-stone-500">
                Démarrez la conversation en écrivant un message ci-dessous.
              </p>
            ) : (
              messages.map((message, index) => (
                <div
                  key={`${message.role}-${index}-${message.content.slice(0, 16)}`}
                  className={cn(
                    'flex',
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm',
                      message.role === 'user'
                        ? 'rounded-br-sm bg-[#1D95AD] text-white'
                        : 'rounded-bl-sm bg-gray-100 text-stone-800'
                    )}
                  >
                    {message.role === 'assistant' ? (
                      <div className="space-y-2 break-words">
                        <ChatMarkdown content={message.content} />
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap break-words">{message.content}</p>
                    )}
                  </div>
                </div>
              ))
            )}
            {error ? (
              <p className="mt-2 text-xs text-red-500">
                {error}
                {error && !error.endsWith('.') ? '.' : null} Veuillez réessayer.
              </p>
            ) : null}
            <div ref={messagesEndRef} />
          </div>

          <form
            className="border-t border-gray-200 bg-gray-50 px-3 py-2"
            onSubmit={(event) => void handleSubmit(event)}
          >
            <div className="flex items-end gap-2">
              <textarea
                aria-label="Votre message"
                className="min-h-[2.25rem] max-h-24 flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-stone-900 shadow-sm outline-none ring-0 placeholder:text-stone-400 focus:border-[#1D95AD] focus:ring-2 focus:ring-[#1D95AD]/20"
                disabled={isSending}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    void handleSubmit(event as unknown as React.FormEvent)
                  }
                }}
                placeholder="Écrivez votre message..."
                rows={1}
                value={input}
              />
              <button
                aria-label="Envoyer le message"
                className={cn(
                  'inline-flex items-center justify-center rounded-lg bg-[#1D95AD] px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#1a8599]',
                  isSending || !input.trim()
                    ? 'cursor-not-allowed opacity-60 hover:bg-[#1D95AD]'
                    : ''
                )}
                disabled={isSending || !input.trim()}
                type="submit"
              >
                {isSending ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <button
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Fermer le widget de chat' : 'Ouvrir le widget de chat'}
        className={cn(
          'fixed bottom-6 right-6 z-40 flex h-16 w-16 items-center justify-center rounded-full bg-[#1D95AD] text-white shadow-xl transition-all duration-300 hover:cursor-pointer hover:bg-[#1a8599]',
          'border border-gray-800/10 backdrop-blur-sm',
          className
        )}
        onClick={toggleOpen}
        type="button"
      >
        <MessageCircle className="h-8 w-8" />
      </button>
    </>
  )
}

function ChatMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ children, ...props }) => (
          <a
            {...props}
            className="underline underline-offset-2 hover:opacity-90"
            rel="noreferrer"
            target="_blank"
          >
            {children}
          </a>
        ),
        code: ({ className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className ?? '')
          const isBlock = Boolean(match)
          return isBlock ? (
            <code
              {...props}
              className="block whitespace-pre-wrap rounded-md bg-black/90 px-3 py-2 font-mono text-[12px] leading-relaxed text-white"
            >
              {children}
            </code>
          ) : (
            <code
              {...props}
              className="rounded bg-black/10 px-1 py-0.5 font-mono text-[12px]"
            >
              {children}
            </code>
          )
        },
        ol: ({ children, ...props }) => (
          <ol {...props} className="list-decimal space-y-1 pl-5">
            {children}
          </ol>
        ),
        p: ({ children, ...props }) => (
          <p {...props} className="whitespace-pre-wrap">
            {children}
          </p>
        ),
        ul: ({ children, ...props }) => (
          <ul {...props} className="list-disc space-y-1 pl-5">
            {children}
          </ul>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

